import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import showToast from '../../utils/toastHelper';

const Reports = () => {
  const [exporting, setExporting] = useState(null);

  const exportData = async (type) => {
    setExporting(type);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/export?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data.data;
      
      // Convert to Excel
      if (data && data.length > 0) {
        exportToExcel(data, type);
        showToast(`${type} data exported successfully`, 'success');
      } else {
        showToast(`No ${type} data available to export`, 'info');
      }
    } catch (error) {
      console.error(`Failed to export ${type}:`, error);
      showToast(`Failed to export ${type} data`, 'error');
    } finally {
      setExporting(null);
    }
  };

  const exportToExcel = (data, type) => {
    if (!data || data.length === 0) return;

    let excelData = [];

    switch (type) {
      case 'votes':
        excelData = data.map((vote, index) => ({
          '#': index + 1,
          'Candidate': vote.candidate?.user?.fullName || 'N/A',
          'Position': vote.position?.name || 'N/A',
          'Election': vote.position?.election?.name || 'N/A',
          'Voted At': new Date(vote.votedAt).toLocaleString()
        }));
        break;

      case 'voters': {
        // Anonymous turnout stats — count unique voters per election/position
        const turnoutMap = {};
        const uniqueVoterIds = new Set();
        data.forEach(vote => {
          const electionName = vote.position?.election?.name || 'Unknown';
          const positionName = vote.position?.name || 'Unknown';
          const key = `${electionName}|||${positionName}`;
          if (!turnoutMap[key]) {
            turnoutMap[key] = { election: electionName, position: positionName, totalVotes: 0, uniqueVoters: new Set() };
          }
          turnoutMap[key].totalVotes++;
          turnoutMap[key].uniqueVoters.add(vote.voterId);
          uniqueVoterIds.add(vote.voterId);
        });

        excelData = Object.values(turnoutMap).map(row => ({
          'Election': row.election,
          'Position': row.position,
          'Unique Voters': row.uniqueVoters.size,
          'Total Votes Cast': row.totalVotes,
        }));

        // Add a summary row
        if (excelData.length > 0) {
          excelData.push({
            'Election': 'TOTAL',
            'Position': '',
            'Unique Voters': uniqueVoterIds.size,
            'Total Votes Cast': data.length,
          });
        }
        break;
      }

      case 'transactions':
        excelData = data.map(txn => ({
          'Transaction ID': txn.id,
          'User': txn.user?.fullName || 'N/A',
          'Amount': txn.amount || 0,
          'Currency': txn.currency || 'UGX',
          'Status': txn.status,
          'Position': txn.position?.name || 'N/A',
          'Date': new Date(txn.createdAt).toLocaleString()
        }));
        break;

      default:
        return;
    }

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, type.charAt(0).toUpperCase() + type.slice(1));
    
    // Generate filename
    const fileName = `${type}_report_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, fileName);
  };

  const reportTypes = [
    {
      id: 'votes',
      title: 'Votes Report',
      description: 'Export all votes showing candidates, positions, and timestamps (voter identity protected)',
      icon: 'bi-check2-square',
      color: '#667eea',
    },
    {
      id: 'voters',
      title: 'Voter Turnout Report',
      description: 'Export voter participation data and turnout statistics',
      icon: 'bi-people',
      color: '#10b981',
    },
    {
      id: 'transactions',
      title: 'Transactions Report',
      description: 'Export all payment transactions for multi-voting',
      icon: 'bi-cash-stack',
      color: '#f59e0b',
    },
  ];

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="mb-4">
        <h3 className="mb-1" style={{ fontWeight: '600' }}>Reports & Exports</h3>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Generate and download reports in Excel format
        </p>
      </div>

      {/* Info Card */}
      <div className="card mb-4" style={{ borderLeft: '4px solid #3b82f6' }}>
        <div className="card-body">
          <div className="d-flex align-items-start">
            <i className="bi bi-info-circle me-3" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
            <div>
              <h6 style={{ fontWeight: '600', color: '#1e40af' }}>About Reports</h6>
              <p className="mb-0" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Click on any report card below to download the data as an Excel file. The file will include all relevant information and can be opened in Excel, Google Sheets, or any spreadsheet application.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Cards */}
      <div className="row">
        {reportTypes.map((report) => (
          <div key={report.id} className="col-md-4 mb-4">
            <div
              className="card h-100"
              style={{
                cursor: exporting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                opacity: exporting && exporting !== report.id ? 0.6 : 1
              }}
              onClick={() => !exporting && exportData(report.id)}
              onMouseEnter={(e) => {
                if (!exporting) {
                  e.currentTarget.style.borderColor = report.color;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div className="card-body text-center p-4">
                <div
                  className="mb-3"
                  style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    background: `${report.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto'
                  }}
                >
                  <i className={report.icon} style={{ fontSize: '2rem', color: report.color }}></i>
                </div>
                <h5 className="mb-2" style={{ fontWeight: '700', color: '#2c3e50' }}>
                  {report.title}
                </h5>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem', lineHeight: '1.6' }}>
                  {report.description}
                </p>
                <button
                  className="btn btn-sm"
                  style={{
                    background: report.color,
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1.5rem',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}
                  disabled={exporting !== null}
                >
                  {exporting === report.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-file-earmark-excel me-2"></i>
                      Export Excel
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Information */}
      <div className="row mt-4">
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-white">
              <h6 className="mb-0" style={{ fontWeight: '600' }}>
                <i className="bi bi-question-circle me-2" style={{ color: '#667eea' }}></i>
                What's Included
              </h6>
            </div>
            <div className="card-body">
              <ul className="mb-0" style={{ fontSize: '0.875rem', lineHeight: '2' }}>
                <li><strong>Votes Report:</strong> All vote records with candidates, positions, and timestamps (voter identity protected for confidentiality)</li>
                <li><strong>Voter Turnout:</strong> Participation statistics showing who voted and how many times</li>
                <li><strong>Transactions Report:</strong> Payment history for multi-voting features</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-6 mb-4">
          <div className="card">
            <div className="card-header bg-white">
              <h6 className="mb-0" style={{ fontWeight: '600' }}>
                <i className="bi bi-shield-check me-2" style={{ color: '#10b981' }}></i>
                Data Privacy
              </h6>
            </div>
            <div className="card-body">
              <p className="mb-0" style={{ fontSize: '0.875rem', lineHeight: '1.8', color: '#6b7280' }}>
                All exported data is sensitive and should be handled according to your organization's data protection policies. 
                Ensure files are stored securely and access is restricted to authorized personnel only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
